import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import { setAlert } from 'redux/alert';
import type { OrganizationAgreementInterface } from 'types';

/** Agreement tab remounts this list for the organization in the route. */
export const ORGANIZATION_AGREEMENT_STALE_MS = 5 * 60 * 1000;

export const organizationAgreementKeys = {
    all: ['organizationAgreements'] as const,
    list: (serviceProviderId: string) => ['organizationAgreements', serviceProviderId] as const,
};

export const organizationAgreementListQuery = (serviceProviderId: string) => ({
    queryKey: organizationAgreementKeys.list(serviceProviderId),
    queryFn: async (): Promise<OrganizationAgreementInterface[]> => {
        const response =
            await api.clientServiceProviders.getOrganizationAgreements(serviceProviderId);
        return response.data ?? [];
    },
    staleTime: ORGANIZATION_AGREEMENT_STALE_MS,
    retry: false,
});

/** One toast per failed fetch when the agreements screen remounts onto a cached error. */
let lastReportedOrganizationAgreementErrorAt = 0;

export interface UseOrganizationAgreementsOptions {
    enabled?: boolean;
    alertOnError?: boolean;
}

/**
 * GET /client-service-providers/:id/agreements
 * Cached per organization so returning to the agreement tab does not refetch.
 */
export const useOrganizationAgreements = (
    serviceProviderId?: string | null,
    options?: UseOrganizationAgreementsOptions
) => {
    const dispatch = useDispatch();
    const id = serviceProviderId ?? '';
    const alertOnError = options?.alertOnError ?? false;
    const query = useQuery({
        ...organizationAgreementListQuery(id),
        enabled: (options?.enabled ?? true) && Boolean(id),
    });
    const { errorUpdatedAt, isError, isFetching } = query;

    useEffect(() => {
        if (!alertOnError || !isError || isFetching || errorUpdatedAt === 0) {
            return;
        }
        if (lastReportedOrganizationAgreementErrorAt === errorUpdatedAt) {
            return;
        }
        lastReportedOrganizationAgreementErrorAt = errorUpdatedAt;
        dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
    }, [alertOnError, dispatch, errorUpdatedAt, isError, isFetching]);

    return query;
};
